import { pickFeatured, type ResearchedInsight } from '@nav/shared';
import type { ReactNode } from 'react';

import { AnimatedContent } from '@/components/react-bits/AnimatedContent';
import { AnimatedList } from '@/components/react-bits/AnimatedList';
import { ShinyText } from '@/components/react-bits/ShinyText';
import { SpotlightCard } from '@/components/react-bits/SpotlightCard';
import { formatDateId } from '@/lib/format';
import { BRAND, EDITORIAL_SHADOW } from '../palette';
import { toInsightItem, type InsightItem, type InsightTone } from '../marketContext';

const TONE_BORDER: Record<InsightTone, string> = {
  negative: BRAND.coral,
  positive: BRAND.mint,
  neutral: BRAND.charcoal,
  accent: BRAND.red,
};

/** Spotlight wash per tone — a hint of the border colour, never a glow. */
const TONE_SPOTLIGHT: Record<InsightTone, string> = {
  negative: 'rgba(230,80,80,0.10)',
  positive: 'rgba(23,147,107,0.10)',
  accent: 'rgba(234,27,26,0.08)',
  neutral: 'rgba(124,91,217,0.08)',
};

/**
 * Three provenance tiers, three visibly distinct badges.
 *
 * "Riset AI" must never be styled to look like "Data Live". A computed figure is
 * arithmetic on uploaded data; a researched claim is a model's reading of the
 * news, checked and approved but still a weaker kind of statement. A reader who
 * cannot tell them apart at a glance is the failure this design exists to avoid,
 * so the colours are deliberately different families rather than shades.
 */
const BADGE: Record<InsightItem['source'], { label: string; fg: string; bg: string; title: string }> = {
  auto: {
    label: 'Data Live',
    fg: BRAND.mint,
    bg: 'rgba(23,147,107,0.10)',
    title: 'Dihitung otomatis dari data NAV live, bukan AI',
  },
  editorial: {
    label: 'Editorial',
    fg: BRAND.textFaint,
    bg: BRAND.track,
    title: 'Konteks pasar, disusun manual',
  },
  researched: {
    label: 'Riset AI',
    fg: '#7C5BD9',
    bg: 'rgba(124,91,217,0.10)',
    title:
      'Disusun oleh Claude dari penelusuran sumber publik, diperiksa otomatis terhadap data NAV, dan disetujui analis',
  },
};

const KICKER_STYLE = {
  fontFamily: 'var(--font-sans)',
  fontSize: 10,
  letterSpacing: '1.5px',
  color: BRAND.textFaint,
} as const;

function renderBody(body: string): ReactNode {
  const parts = body.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <b key={i} style={{ color: BRAND.ink, fontWeight: 600 }}>
          {part.slice(2, -2)}
        </b>
      );
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <i key={i}>{part.slice(1, -1)}</i>;
    }
    return <span key={i}>{part}</span>;
  });
}

function Badge({ source, shiny }: { source: InsightItem['source']; shiny?: boolean }) {
  const badge = BADGE[source];
  const label = shiny ? (
    <ShinyText color={badge.fg} highlight="#C9BDF2" speed={2.4}>
      {badge.label}
    </ShinyText>
  ) : (
    badge.label
  );
  return (
    <span
      className="uppercase shrink-0 rounded-full px-2 py-0.5"
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 8.5,
        letterSpacing: '0.5px',
        fontWeight: 700,
        color: badge.fg,
        background: badge.bg,
        lineHeight: 1.4,
      }}
      title={badge.title}
    >
      {label}
    </span>
  );
}

function Citations({ item, animated }: { item: InsightItem; animated: boolean }) {
  const cites = item.citations ?? [];
  if (cites.length === 0) return null;
  const links = [
    <span key="label">Sumber:</span>,
    ...cites.map((cite) => (
      <a
        key={cite.url}
        href={cite.url}
        target="_blank"
        // noopener/noreferrer is not optional here: the href came from a web
        // search result, not from us.
        rel="noopener noreferrer"
        title={cite.title}
        style={{ color: BRAND.red, textDecoration: 'underline' }}
      >
        {cite.publisher}
      </a>
    )),
  ];
  const cls = 'flex flex-wrap items-center gap-x-2 gap-y-1';
  return animated ? <AnimatedList className={cls}>{links}</AnimatedList> : <div className={cls}>{links}</div>;
}

/**
 * One insight card. The anatomy is the same for a featured Riset AI card and
 * an editorial card; `featured` only switches on the quiet motion (shiny badge,
 * staggered citations) and the tone-tinted spotlight.
 */
function InsightCard({ item, featured = false }: { item: InsightItem; featured?: boolean }) {
  const hasFooter = (item.citations?.length ?? 0) > 0 || Boolean(item.generatedAt);
  return (
    <div
      className="h-full"
      style={{
        background: BRAND.panel,
        border: `1px solid ${BRAND.panelBorder}`,
        borderLeft: `3px solid ${TONE_BORDER[item.tone]}`,
        borderRadius: 8,
        boxShadow: EDITORIAL_SHADOW,
        overflow: 'hidden',
      }}
    >
      <SpotlightCard className="h-full" spotlightColor={TONE_SPOTLIGHT[item.tone]}>
        <article className="px-6 py-[22px]">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="uppercase" style={KICKER_STYLE}>
              {item.kicker}
            </p>
            <Badge source={item.source} shiny={featured && item.source === 'researched'} />
          </div>
          <h3
            className="mb-2.5"
            style={{
              fontSize: 17,
              color: BRAND.red,
              lineHeight: 1.3,
              fontWeight: 400,
              fontFamily: 'var(--font-display)',
            }}
          >
            {item.title}
          </h3>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 13, lineHeight: 1.65, color: BRAND.textDim }}>
            {renderBody(item.body)}
          </p>

          {hasFooter ? (
            <div
              className="mt-3 pt-3"
              style={{
                borderTop: `1px solid ${BRAND.panelBorder}`,
                fontFamily: 'var(--font-sans)',
                fontSize: 11,
                color: BRAND.textFaint,
                lineHeight: 1.6,
              }}
            >
              <Citations item={item} animated={featured} />
              {item.generatedAt && (
                // A published card can be weeks old — the review gate is manual
                // by design. The reader has to be able to see how old.
                <div className="mt-1">Disusun {formatDateId(item.generatedAt.slice(0, 10))}</div>
              )}
            </div>
          ) : null}
        </article>
      </SpotlightCard>
    </div>
  );
}

/**
 * The two Riset AI slots: macro (why the world moved) left, fund (what the
 * series did) right. `pickFeatured` keeps only the newest published card per
 * kind, so a card awaiting supersede never shows beside its replacement.
 * One card alone takes a single column capped at 720px; none omits the row.
 */
function FeaturedInsightRow({ researched }: { researched: readonly ResearchedInsight[] }) {
  const { macro, fund } = pickFeatured(researched);
  const cards = [macro, fund].filter((c): c is ResearchedInsight => c !== null);
  if (cards.length === 0) return null;
  return (
    <div className={`sfl-featured-row${cards.length === 1 ? ' is-single' : ''}`}>
      {cards.map((card, i) => (
        <AnimatedContent key={card.id} delay={i * 0.1} className="h-full">
          <InsightCard item={toInsightItem(card)} featured />
        </AnimatedContent>
      ))}
    </div>
  );
}

/** `Tren YoY · Data Live` → `Tren YoY`; the badge beside it already says so. */
function stripKicker(kicker: string): string {
  return kicker.replace(/\s*·\s*Data Live\s*$/i, '');
}

function toneGlyph(tone: InsightTone): { glyph: string; color: string } | null {
  if (tone === 'negative') return { glyph: '▼', color: BRAND.coral };
  if (tone === 'positive') return { glyph: '▲', color: BRAND.mint };
  return null;
}

/**
 * Every computed (`auto`) card as one line: kicker + badge, then the card's
 * title as the value phrase. The body is a restatement of the title with the
 * same numbers, so it lives in the tooltip rather than on screen. Same visual
 * family as `SflMacroStrip`: white cells, hairline dividers, 10px radius.
 */
function FactsStrip({ items }: { items: InsightItem[] }) {
  if (items.length === 0) return null;
  return (
    <div className="sfl-facts-strip">
      {items.map((item) => {
        const glyph = toneGlyph(item.tone);
        return (
          <div key={item.title} className="sfl-facts-row" title={item.body.replace(/\*\*/g, '')}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="uppercase truncate" style={{ ...KICKER_STYLE, letterSpacing: '1px' }}>
                {stripKicker(item.kicker)}
              </span>
              <Badge source={item.source} />
            </div>
            <p
              className="sfl-facts-value"
              style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, color: BRAND.ink, lineHeight: 1.45 }}
            >
              {item.title}
            </p>
            <span
              aria-hidden="true"
              className="sfl-facts-glyph"
              style={{ fontSize: 11, color: glyph?.color ?? 'transparent', width: 14, textAlign: 'right' }}
            >
              {glyph?.glyph ?? ''}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Insight & Analisa, top to bottom:
 *   1. Featured row — newest published Riset AI macro + fund card.
 *   2. Facts strip — every Data Live item, one line each.
 *   3. Editorial cards, if any, in the old two-column grid.
 *
 * `researched` is the raw published list so `pickFeatured` can apply the
 * newest-per-kind rule; `items` carries the editorial and computed tiers.
 */
export function SflInsightGrid({
  researched,
  items,
}: {
  researched: readonly ResearchedInsight[];
  items: InsightItem[];
}) {
  const facts = items.filter((i) => i.source === 'auto');
  const editorial = items.filter((i) => i.source === 'editorial');
  return (
    <div className="flex flex-col gap-5">
      <FeaturedInsightRow researched={researched} />
      <FactsStrip items={facts} />
      {editorial.length > 0 && (
        <div className="sfl-insight-grid">
          {editorial.map((item) => (
            <InsightCard key={item.title} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
